import styled from '@emotion/styled';
import { Card, Icon } from '../';
import type { Theme } from '../../theme';
import type { Folder } from '../../types/folder';
import { isRestrictedAsset } from '../../utils';

export interface FolderCardProps {
  /** Folder data */
  folder: Folder;
  /** Layout mode */
  layout?: 'card' | 'list';
  /** Whether the folder is selected */
  selected?: boolean;
  /** Callback when folder is selected */
  onSelect?: (id: string) => void;
  /** Callback when folder is clicked */
  onClick?: (folder: Folder) => void;
  /** Callback when folder is double-clicked */
  onDoubleClick?: (folder: Folder) => void;
  /** Custom className */
  className?: string;
  /** Test ID for testing (deprecated, use dataTestId) */
  'data-testid'?: string;
  /** Test identifier for automated testing */
  dataTestId?: string;
  /** Data identifier for ib-ui compatibility */
  dataId?: string;
}

const ListContainer = styled.div<{ $selected: boolean; $restricted: boolean }>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => (theme as Theme).spacing[6]};
  padding: ${({ theme }) => (theme as Theme).spacing[6]};
  background-color: ${({ theme }) => (theme as Theme).colors.white};
  border: ${({ theme }) => (theme as Theme).borders.width.medium} solid ${({ $selected, theme }) => ($selected ? (theme as Theme).colors.highlight : 'transparent')};
  border-radius: ${({ theme }) => (theme as Theme).borders.radius.sm};
  cursor: ${({ $restricted }) => ($restricted ? 'not-allowed' : 'pointer')};
  transition: ${({ theme }) => (theme as Theme).transitions.fast};
  opacity: ${({ $restricted }) => ($restricted ? 0.6 : 1)};

  &:hover {
    background-color: ${({ $restricted, theme }) => ($restricted ? (theme as Theme).colors.white : (theme as Theme).colors.border.light)};
    box-shadow: ${({ $restricted, theme }) => ($restricted ? 'none' : (theme as Theme).shadows.dropdown)};
  }

  &:focus-visible {
    outline: ${({ theme }) => (theme as Theme).borders.width.medium} solid ${({ theme }) => (theme as Theme).colors.highlight};
    outline-offset: 2px;
  }
`;

const MediaContainer = styled.div`
  position: relative;
`;

const IconContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  aspect-ratio: 16 / 9;
  background-color: ${({ theme }) => (theme as Theme).colors.border.light};
  color: ${({ theme }) => (theme as Theme).colors.text.muted};
`;

const ListThumbnail = styled.div`
  flex-shrink: 0;
  width: 64px;
  height: 64px;
  border-radius: ${({ theme }) => (theme as Theme).borders.radius.md};
  overflow: hidden;
  background-color: ${({ theme }) => (theme as Theme).colors.border.light};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ListThumbnailImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const SelectedBadge = styled.div`
  position: absolute;
  top: ${({ theme }) => (theme as Theme).spacing[3]};
  right: ${({ theme }) => (theme as Theme).spacing[3]};
  background-color: ${({ theme }) => (theme as Theme).colors.highlight};
  color: ${({ theme }) => (theme as Theme).colors.white};
  border-radius: ${({ theme }) => (theme as Theme).borders.radius.full};
  padding: ${({ theme }) => (theme as Theme).spacing[1]};
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: ${({ theme }) => (theme as Theme).shadows.dropdown};
`;

const RestrictedBadge = styled.div`
  position: absolute;
  top: ${({ theme }) => (theme as Theme).spacing[3]};
  left: ${({ theme }) => (theme as Theme).spacing[3]};
  background-color: ${({ theme }) => (theme as Theme).colors.primary};
  color: ${({ theme }) => (theme as Theme).colors.white};
  border-radius: ${({ theme }) => (theme as Theme).borders.radius.full};
  padding: ${({ theme }) => (theme as Theme).spacing[1]};
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: ${({ theme }) => (theme as Theme).shadows.dropdown};
`;

const ContentInner = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => (theme as Theme).spacing[1]};
`;

const ListContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const Title = styled.h3`
  font-size: ${({ theme }) => (theme as Theme).typography.fontSize.base};
  font-weight: ${({ theme }) => (theme as Theme).typography.fontWeight.bold};
  color: ${({ theme }) => (theme as Theme).colors.text.secondary};
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Metadata = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => (theme as Theme).spacing[1]};
`;

const Count = styled.p`
  font-size: ${({ theme }) => (theme as Theme).typography.fontSize.xs};
  color: ${({ theme }) => (theme as Theme).colors.text.secondary};
  margin: 0;
`;

const EmptyText = styled.p`
  font-size: ${({ theme }) => (theme as Theme).typography.fontSize.xs};
  color: ${({ theme }) => (theme as Theme).colors.text.muted};
  font-style: italic;
  margin: 0;
`;

const ListNav = styled.div`
  flex-shrink: 0;
  color: ${({ theme }) => (theme as Theme).colors.border.medium};
  display: flex;
  align-items: center;
`;

/**
 * FolderCard component for displaying DAM folders
 * Uses Card UI primitive from @intelligencebank/ui-primitives
 * Based on IntelligenceBank foldercard.jsx
 */
export const FolderCard = ({
  folder,
  layout = 'card',
  selected = false,
  onSelect,
  onClick,
  onDoubleClick,
  className = '',
  'data-testid': testId,
  dataTestId,
  dataId,
}: FolderCardProps) => {
  const isRestricted = isRestrictedAsset(folder.isPublic, folder.allowedActions);
  const isEmpty = folder.foldersCount === 0 && folder.resourcesCount === 0;

  const handleClick = () => {
    if (isRestricted) return;

    if (onSelect) {
      onSelect(folder.id);
    }
    if (onClick) {
      onClick(folder);
    }
  };

  const handleDoubleClick = () => {
    if (isRestricted) return;

    if (onDoubleClick) {
      onDoubleClick(folder);
    }
  };

  const renderCardMedia = () => {
    if (folder.thumbnail) {
      return <Card.Media src={folder.thumbnail} alt={folder.name} aspectRatio="16:9" />;
    }

    return (
      <IconContainer>
        <Icon name="folder" size="xl" />
      </IconContainer>
    );
  };

  const renderListThumbnail = () => {
    if (folder.thumbnail) {
      return <ListThumbnailImage src={folder.thumbnail} alt={folder.name} loading="lazy" />;
    }

    return <Icon name="folder" size="lg" />;
  };

  const renderCounts = () => {
    if (isEmpty || isRestricted) {
      return <EmptyText>Empty</EmptyText>;
    }

    return (
      <>
        {folder.foldersCount > 0 && (
          <Count>
            {folder.foldersCount} sub-folder{folder.foldersCount !== 1 ? 's' : ''}
          </Count>
        )}
        {folder.resourcesCount > 0 && (
          <Count>
            {folder.resourcesCount} item{folder.resourcesCount !== 1 ? 's' : ''}
          </Count>
        )}
      </>
    );
  };

  if (layout === 'list') {
    return (
      <ListContainer
        className={className}
        $selected={selected}
        $restricted={isRestricted}
        onClick={handleClick}
        onDoubleClick={handleDoubleClick}
        role="button"
        tabIndex={0}
        aria-label={`Folder: ${folder.name}`}
        aria-disabled={isRestricted}
        data-testid={dataTestId || testId}
        data-id={dataId}
      >
        <ListThumbnail>{renderListThumbnail()}</ListThumbnail>
        <ListContent>
          <Title>{folder.name}</Title>
          <Metadata>{renderCounts()}</Metadata>
        </ListContent>
        <ListNav>
          <Icon name="chevron_right" size="md" />
        </ListNav>
      </ListContainer>
    );
  }

  return (
    <Card
      variant="elevated"
      interactive
      selected={selected}
      disabled={isRestricted}
      onClick={handleClick}
      className={className}
      aria-label={`Folder: ${folder.name}`}
      data-testid={dataTestId || testId}
      data-id={dataId}
      onDoubleClick={handleDoubleClick}
    >
      <MediaContainer>
        {renderCardMedia()}
        {selected && (
          <SelectedBadge>
            <Icon name="check_circle" size="md" />
          </SelectedBadge>
        )}
        {isRestricted && (
          <RestrictedBadge>
            <Icon name="lock" size="sm" />
          </RestrictedBadge>
        )}
      </MediaContainer>

      <Card.Content>
        <ContentInner>
          <Title>{folder.name}</Title>
          <Metadata>{renderCounts()}</Metadata>
        </ContentInner>
      </Card.Content>
    </Card>
  );
};

FolderCard.displayName = 'FolderCard';